import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Package, 
  Plus, 
  Search, 
  Pencil, 
  Trash2,
  AlertTriangle,
  Filter
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialFormData = {
  nom: "",
  categorie: "autre",
  quantite: "",
  unite: "pieces",
  prix_unitaire: "",
  seuil_alerte: 10,
  fournisseur: ""
};

export default function Materiaux() {
  const [materiaux, setMateriaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMateriau, setEditingMateriau] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const { t } = useLanguage();

  const CATEGORIES = [
    { value: "bois", label: t('wood') },
    { value: "acier", label: t('steel') },
    { value: "beton", label: t('concrete') },
    { value: "electricite", label: t('electricity') },
    { value: "plomberie", label: t('plumbing') },
    { value: "autre", label: t('other') }
  ];

  const UNITES = [
    { value: "kg", label: t('kilograms') },
    { value: "m", label: t('meters') },
    { value: "m2", label: t('squareMeters') },
    { value: "m3", label: t('cubicMeters') },
    { value: "pieces", label: t('pieces') }
  ];

  useEffect(() => {
    fetchMateriaux();
  }, []);

  const fetchMateriaux = async () => {
    try {
      const response = await axios.get(`${API}/materiaux`);
      setMateriaux(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des matériaux");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMateriau) {
        await axios.put(`${API}/materiaux/${editingMateriau.id}`, {
          ...formData,
          quantite: parseFloat(formData.quantite) || 0,
          prix_unitaire: parseFloat(formData.prix_unitaire) || 0
        });
        toast.success("Matériau mis à jour");
      } else {
        await axios.post(`${API}/materiaux`, {
          ...formData,
          quantite: parseFloat(formData.quantite) || 0,
          prix_unitaire: parseFloat(formData.prix_unitaire) || 0
        });
        toast.success("Matériau ajouté avec succès");
      }
      setDialogOpen(false);
      setFormData(initialFormData);
      setEditingMateriau(null);
      fetchMateriaux();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleEdit = (materiau) => {
    setEditingMateriau(materiau);
    setFormData({
      ...materiau,
      quantite: materiau.quantite !== undefined ? String(materiau.quantite) : "",
      prix_unitaire: materiau.prix_unitaire !== undefined ? String(materiau.prix_unitaire) : "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer ce matériau ?")) {
      try {
        await axios.delete(`${API}/materiaux/${id}`);
        toast.success("Matériau supprimé");
        fetchMateriaux();
      } catch (error) {
        toast.error("Erreur lors de la suppression");
      }
    }
  };

  const openNewDialog = () => {
    setEditingMateriau(null);
    setFormData(initialFormData);
    setDialogOpen(true);
  };

  const filteredMateriaux = materiaux.filter(m => {
    const matchesSearch = m.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         m.fournisseur?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === "all" || m.categorie === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const getCategoryLabel = (cat) => CATEGORIES.find(c => c.value === cat)?.label || cat;
  const isLowStock = (materiau) => materiau.quantite <= materiau.seuil_alerte;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <div className="w-8 h-8 border-2 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="materiaux-page" className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1E293B] uppercase tracking-tight font-['Barlow_Condensed']">Matériaux</h1>
          <p className="text-[#94A3B8] mt-1">Gestion de votre inventaire</p>
        </div>
        <Button 
          data-testid="btn-new-materiau"
          onClick={openNewDialog}
          className="bg-[#F97316] hover:bg-orange-600 text-white rounded-lg uppercase tracking-wide text-sm font-medium btn-press shadow-md shadow-orange-200"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau matériau
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
          <Input
            data-testid="search-materiaux"
            placeholder="Rechercher un matériau..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white border-[#E2E8F0] text-[#1E293B] placeholder:text-[#CBD5E1] rounded-lg focus:ring-[#3B82F6] shadow-sm"
          />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger data-testid="filter-category" className="w-[180px] bg-white border-[#E2E8F0] text-[#1E293B] rounded-lg shadow-sm">
            <Filter className="w-4 h-4 mr-2 text-[#94A3B8]" />
            <SelectValue placeholder="Catégorie" />
          </SelectTrigger>
          <SelectContent className="bg-white border-[#E2E8F0] text-[#1E293B] shadow-lg">
            <SelectItem value="all">Toutes catégories</SelectItem>
            {CATEGORIES.map(cat => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="bg-white border-[#E2E8F0] overflow-hidden shadow-sm">
        {filteredMateriaux.length === 0 ? (
          <CardContent className="p-12 text-center">
            <Package className="w-16 h-16 mx-auto mb-4 text-[#CBD5E1]" />
            <h3 className="text-xl font-semibold text-[#1E293B] mb-2">Aucun matériau</h3>
            <p className="text-[#94A3B8] mb-4">Commencez par ajouter votre premier matériau</p>
            <Button 
              onClick={openNewDialog}
              className="bg-[#F97316] hover:bg-orange-600 text-white rounded-lg"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un matériau
            </Button>
          </CardContent>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-[#F1F5F9] hover:bg-transparent bg-[#F8FAFC]">
                <TableHead className="text-[#64748B] font-mono text-xs uppercase tracking-wider">Nom</TableHead>
                <TableHead className="text-[#64748B] font-mono text-xs uppercase tracking-wider">Catégorie</TableHead>
                <TableHead className="text-[#64748B] font-mono text-xs uppercase tracking-wider text-right">Quantité</TableHead>
                <TableHead className="text-[#64748B] font-mono text-xs uppercase tracking-wider text-right">Prix unitaire</TableHead>
                <TableHead className="text-[#64748B] font-mono text-xs uppercase tracking-wider text-right">Valeur stock</TableHead>
                <TableHead className="text-[#64748B] font-mono text-xs uppercase tracking-wider">Fournisseur</TableHead>
                <TableHead className="text-[#64748B] font-mono text-xs uppercase tracking-wider text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMateriaux.map((materiau) => (
                <TableRow 
                  key={materiau.id} 
                  data-testid={`materiau-row-${materiau.id}`}
                  className="border-[#F1F5F9] hover:bg-[#F8FAFC] transition-colors"
                >
                  <TableCell className="font-medium text-[#1E293B]">
                    <div className="flex items-center gap-2">
                      {isLowStock(materiau) && (
                        <AlertTriangle className="w-4 h-4 text-[#F97316]" />
                      )}
                      {materiau.nom}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="px-2 py-1 rounded-md text-xs font-medium bg-[#F1F5F9] text-[#475569] border border-[#E2E8F0]">
                      {getCategoryLabel(materiau.categorie)}
                    </span>
                  </TableCell>
                  <TableCell className={`text-right font-mono font-semibold ${isLowStock(materiau) ? 'text-[#F97316]' : 'text-[#1E293B]'}`}>
                    {materiau.quantite} {materiau.unite}
                  </TableCell>
                  <TableCell className="text-right font-mono text-[#1E293B]">
                    {formatCurrency(materiau.prix_unitaire)}/{materiau.unite}
                  </TableCell>
                  <TableCell className="text-right font-mono text-emerald-600 font-semibold">
                    {formatCurrency(materiau.quantite * materiau.prix_unitaire)}
                  </TableCell>
                  <TableCell className="text-[#64748B]">
                    {materiau.fournisseur || "-"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <button
                        data-testid={`btn-edit-materiau-${materiau.id}`}
                        onClick={() => handleEdit(materiau)}
                        className="p-2 text-[#94A3B8] hover:text-[#3B82F6] hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`btn-delete-materiau-${materiau.id}`}
                        onClick={() => handleDelete(materiau.id)}
                        className="p-2 text-[#94A3B8] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-[#E2E8F0] text-[#1E293B] max-w-lg shadow-xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase tracking-tight font-['Barlow_Condensed'] text-[#1E293B]">
              {editingMateriau ? "Modifier le matériau" : "Nouveau matériau"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#475569] font-medium">Nom du matériau *</Label>
              <Input
                data-testid="input-nom-materiau"
                required
                value={formData.nom}
                onChange={(e) => setFormData({...formData, nom: e.target.value})}
                className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg focus:ring-[#3B82F6]"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#475569] font-medium">Catégorie</Label>
                <Select value={formData.categorie} onValueChange={(v) => setFormData({...formData, categorie: v})}>
                  <SelectTrigger className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#E2E8F0] text-[#1E293B] shadow-lg">
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[#475569] font-medium">Unité</Label>
                <Select value={formData.unite} onValueChange={(v) => setFormData({...formData, unite: v})}>
                  <SelectTrigger className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#E2E8F0] text-[#1E293B] shadow-lg">
                    {UNITES.map(u => (
                      <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#475569] font-medium">Quantité *</Label>
                <Input
                  data-testid="input-quantite"
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  value={formData.quantite}
                  placeholder="0"
                  onChange={(e) => setFormData({...formData, quantite: e.target.value})}
                  className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg focus:ring-[#3B82F6]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#475569] font-medium">Prix unitaire (€) *</Label>
                <Input
                  data-testid="input-prix"
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  value={formData.prix_unitaire}
                  placeholder="0"
                  onChange={(e) => setFormData({...formData, prix_unitaire: e.target.value})}
                  className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg focus:ring-[#3B82F6]"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#475569] font-medium">Seuil d'alerte (stock minimum)</Label>
              <Input
                data-testid="input-seuil"
                type="number"
                min="0"
                step="1"
                value={formData.seuil_alerte}
                onChange={(e) => setFormData({...formData, seuil_alerte: parseFloat(e.target.value) || 0})}
                className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg focus:ring-[#3B82F6]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#475569] font-medium">Fournisseur</Label>
              <Input
                data-testid="input-fournisseur"
                value={formData.fournisseur}
                onChange={(e) => setFormData({...formData, fournisseur: e.target.value})}
                className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg focus:ring-[#3B82F6]"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9] rounded-lg"
              >
                Annuler
              </Button>
              <Button
                data-testid="btn-submit-materiau"
                type="submit"
                className="bg-[#F97316] hover:bg-orange-600 text-white rounded-lg shadow-md shadow-orange-200"
              >
                {editingMateriau ? "Mettre à jour" : "Ajouter"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
